import React, { useEffect } from 'react';
import {
  Button,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  List,
  ListItem,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { StatusBadge } from '../../../components/StatusBadge';
import { Transaction } from '../../../domain/transaction';
import { Formatter } from '../../../utils/Formatter';
import { paymentMethodToString } from '../../../domain/payment-method';

interface TransactionDetailProps {
  transaction: Transaction;
  showModal: boolean;
  close: () => void;
}

export const TransactionDetailModal = ({ transaction, showModal, close }: TransactionDetailProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (showModal) {
      onOpen();
    } else {
      onClose();
    }
  }, [showModal]);



  const closeModal = () => {
    close();
  };

  return (
    <Drawer isOpen={isOpen} onClose={() => closeModal()} size="sm">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader textTransform="capitalize">
          Operación {transaction.operation_number}
          <DrawerCloseButton></DrawerCloseButton>
        </DrawerHeader>
        <TransactionDetailModalContent transaction={transaction} />
      </DrawerContent>
    </Drawer>
  );
};

export const TransactionDetailModalContent = ({ transaction }: { transaction: Transaction }) => {

  const handleDownload = () => { }

  const capitalizeText = (text: string) => {
    return text
      .split(' ')
      .map((word, index) => {
        return index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : word.toLowerCase();
      })
      .join(' ');
  };

  const calculateTransactionTaxesAndFees = (transaction: Transaction) => {
    return Formatter.toCurrency(transaction.total_taxes_and_fees ||
      (transaction.vat_on_merchant_fee + transaction.total_merchant_fee), transaction.currency)
  }

  return (
    <Flex paddingX="xxxl" flexDirection="column" height="full" overflowY="auto">
      <Flex flexDirection="column" alignItems="center" borderTopWidth="normal" paddingY="xxl">
        <Text fontSize="l" fontWeight="bold" marginBottom="l">Estado</Text>
        <StatusBadge fontSize="" status={transaction.status} />
        {transaction.status !== 'APPROVED' && transaction.description &&
          <Text fontSize="s" marginTop="l" color="neutral.600" marginBottom="l">{transaction.description}</Text>
        }
      </Flex>

      <List borderTopWidth="normal" paddingY="xxl">
        <DetailItem label="Fecha" value={Formatter.toDate(transaction.date_time).dateString} />
        <DetailItem label="Hora" value={Formatter.toDate(transaction.date_time).timeString} />
        <DetailItem label="N° operación" value={transaction.operation_number} />
        <DetailItem label="N° autorización" value={transaction.authorization_code || '-'} />
        <DetailItem label="N° de termininal" value={transaction.serial_number} />
        <DetailItem label="Comercio" value={transaction.merchant_name} />
        <DetailItem label="Sucursal" value={transaction.branch_name} />
        <DetailItem label="Concepto" value={'-'} />
      </List>

      <List borderTopWidth="normal" paddingY="xxl">
        <Text fontSize="l" fontWeight="bold" marginBottom="xl">Datos del pago</Text>

        <DetailItem label="Operación" value={capitalizeText(Formatter.typeToString(transaction.operation_type))} />
        <DetailItem label="Tarjeta" value={transaction.card_brand} />
        <DetailItem label="Medio de pago" value={capitalizeText(paymentMethodToString(transaction.payment_method_type))} />
        <DetailItem label="Titular" value={transaction.payment_card_owner} />
        <DetailItem label="N° de tarjeta" value={transaction.card_mask} />
        <DetailItem label="Cuotas" value={transaction.installments || '-'} />
      </List>

      <List borderTopWidth="normal" paddingY="xxl">
        <DetailItem label="Monto bruto" value={Formatter.toCurrency(transaction.gross_amount, transaction.currency)} />
        <DetailItem label="IVA + Comisiones" value={calculateTransactionTaxesAndFees(transaction)} />
        <DetailItem label="Propina" value={Formatter.toCurrency(transaction.tip_amount, transaction.currency)} />

        <ListItem
          display="flex"
          justifyContent="space-between"
          marginTop="xxl"
          paddingX="xl"
          paddingY="s"
          borderRadius="md"
          borderWidth="normal"
        >
          <Text fontSize="xl" fontWeight="normal" color="neutral.600">Monto neto</Text>
          <Text fontSize="xl" fontWeight="bold">{Formatter.toCurrency(transaction.net_amount, transaction.currency)}</Text>
        </ListItem>
      </List>

    </Flex>
  );
};


interface DetailItemProps {
  label: string | number;
  value: string | number;
}

const DetailItem = ({ label, value }: DetailItemProps) => {
  return <ListItem
    display="flex"
    justifyContent="space-between"
    marginBottom="l"
  >
    <Text fontSize="s" fontWeight="normal" color="neutral.600">{label}</Text>
    <Text fontSize="s" fontWeight="bold">{value}</Text>
  </ListItem>;
};
